import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useTranslations } from '../hooks/useTranslations';

// ── Cloudflare Turnstile ────────────────────────────────────────────────────
declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, opts: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
    };
  }
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

const inputClass =
  'bg-white border border-border-light focus:border-brand-indigo focus:ring-1 focus:ring-brand-indigo/30 rounded-xl px-4 py-3 text-text-primary placeholder:text-text-muted transition-colors duration-150';

const schema = z.object({
  name:    z.string().min(2, 'Ange minst 2 tecken'),
  email:   z.string().email('Ange en giltig e-postadress'),
  subject: z.string().min(1, 'Välj ett ämne'),
  message: z.string().min(10, 'Ange minst 10 tecken'),
});
type F = z.infer<typeof schema>;

const ContactForm = () => {
  const t = useTranslations();
  const [submitting, setSub] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const turnstileRef  = useRef<HTMLDivElement>(null);
  const widgetIdRef   = useRef<string | null>(null);

  const form = useForm<F>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', subject: '', message: '' },
  });

  // ── Load Turnstile script once ────────────────────────────────────────────
  useEffect(() => {
    const render = () => {
      if (!window.turnstile || !turnstileRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'light',
        size: 'normal',
      });
    };

    if (!document.getElementById('cf-turnstile-script')) {
      const s = document.createElement('script');
      s.id    = 'cf-turnstile-script';
      s.src   = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.onload = render;
      document.head.appendChild(s);
    } else {
      render();
    }
    return () => { widgetIdRef.current = null; };
  }, []);

  const onSubmit = async (data: F) => {
    const token = widgetIdRef.current
      ? window.turnstile?.getResponse(widgetIdRef.current)
      : undefined;

    if (!token) {
      setStatus('error');
      setErrorMsg('Fyll i Turnstile-kontrollen innan du skickar.');
      return;
    }

    setSub(true);
    setStatus('idle');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, turnstileToken: token }),
      });
      const json = await res.json() as { ok?: boolean; error?: string };

      if (!res.ok || !json.ok) {
        throw new Error(json.error ?? 'Okänt fel');
      }

      setStatus('success');
      form.reset();
      if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Okänt fel');
    } finally {
      setSub(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">

        {/* Success banner */}
        {status === 'success' && (
          <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3.5 text-sm text-emerald-800">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
            <span>
              <strong>Skickat!</strong> Vi återkommer inom 24 timmar. Du bör även ha fått en bekräftelse på din e-post.
            </span>
          </div>
        )}

        {/* Error banner */}
        {status === 'error' && (
          <div className="flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3.5 text-sm text-red-800">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
            <span>
              {errorMsg || 'Något gick fel.'}{' '}
              <a
                href={`mailto:info@aifinity.se?subject=${encodeURIComponent(form.getValues('subject'))}&body=${encodeURIComponent(`Från: ${form.getValues('name')}\nE-post: ${form.getValues('email')}\n\n${form.getValues('message')}`)}`}
                className="underline font-semibold"
              >
                Maila info@aifinity.se direkt →
              </a>
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <FormField control={form.control} name="name" render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-text-primary">{t.contactForm.name}</FormLabel>
              <FormControl>
                <Input {...field} className={inputClass} placeholder={t.contactForm.namePlaceholder}
                  aria-label="Ditt namn" disabled={submitting} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )} />

          <FormField control={form.control} name="email" render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-text-primary">{t.contactForm.email}</FormLabel>
              <FormControl>
                <Input {...field} type="email" className={inputClass} placeholder={t.contactForm.emailPlaceholder}
                  aria-label="Din e-postadress" disabled={submitting} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )} />
        </div>

        <FormField control={form.control} name="subject" render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium text-text-primary">Ämne</FormLabel>
            <FormControl>
              <select
                {...field}
                disabled={submitting}
                aria-label="Välj ämne"
                className={`${inputClass} w-full appearance-none cursor-pointer`}
              >
                <option value="">Välj ett ämne…</option>
                <option value="AI-automation">AI-automation</option>
                <option value="SaaS-applikation">SaaS-applikation</option>
                <option value="Klagobot">Klagobot</option>
                <option value="Swishkassa">Swishkassa</option>
                <option value="3D-printing">3D-printing</option>
                <option value="Annat">Annat</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )} />

        <FormField control={form.control} name="message" render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium text-text-primary">{t.contactForm.message}</FormLabel>
            <FormControl>
              <Textarea {...field} className={`${inputClass} min-h-[130px] resize-none`}
                placeholder={t.contactForm.messagePlaceholder}
                aria-label="Ditt meddelande" disabled={submitting} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} />

        {/* Cloudflare Turnstile widget */}
        <div ref={turnstileRef} aria-label="Bot-skydd via Cloudflare Turnstile" />

        <button
          type="submit"
          disabled={submitting}
          aria-label="Skicka meddelande"
          className="group btn-glow relative overflow-hidden w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600
            text-white font-semibold py-3.5 rounded-xl text-sm flex items-center justify-center gap-2
            disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 animate-spin" />{t.contactForm.sending}</>
          ) : (
            <><Send className="w-4 h-4" />{t.contactForm.send}</>
          )}
          <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </button>

        {/* Trust signals */}
        <div className="flex flex-wrap items-center justify-center gap-4 pt-1">
          {['Svar inom 24 timmar', 'Konkret förslag', 'Ingen bindning'].map(text => (
            <span key={text} className="flex items-center gap-1.5 text-[11px] text-text-muted">
              <span className="w-1 h-1 rounded-full bg-emerald-400 inline-block" />
              {text}
            </span>
          ))}
        </div>

      </form>
    </Form>
  );
};

export default ContactForm;
